import * as React from "react"
import { Check, ChevronsUpDown, Loader2, Plus, X } from "lucide-react"
import { useDebounce } from "use-debounce"

import { cn } from "@/lib/utils"
import { useIsReadOnly } from "@/contexts/ReadOnlyContext"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface ComboboxOption {
  value: string
  label: string
  description?: string
  disabled?: boolean
}

interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  isLoading?: boolean
  /**
   * Called with the debounced search text. Passing it switches the list to
   * server-side search — cmdk stops filtering, and the caller owns `options`.
   */
  onInputChange?: (query: string) => void
  /**
   * Let the user commit whatever they typed, even when it isn't in `options`.
   * Needed wherever the option list is a sample of what exists rather than the
   * full set of legal values.
   */
  allowCustomValue?: boolean
  /** Label for the commit-what-I-typed row. Receives the trimmed input. */
  customValueLabel?: (query: string) => string
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select option...",
  emptyText = "No option found.",
  disabled,
  className,
  isLoading = false,
  onInputChange,
  allowCustomValue = false,
  customValueLabel = (query) => `Use "${query}"`,
}: ComboboxProps) {
  const inReadOnlyRegion = useIsReadOnly()
  const isDisabled = disabled || inReadOnlyRegion
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [debouncedInputValue] = useDebounce(inputValue, 500)

  React.useEffect(() => {
    onInputChange?.(debouncedInputValue)
  }, [debouncedInputValue, onInputChange])

  // With `allowCustomValue` the option list is only a sample, so a selected
  // value that isn't in it is normal — show it rather than falling back to the
  // placeholder, which would read as "nothing selected".
  const selectedOption =
    options.find((option) => option.value === value) ??
    (allowCustomValue && value ? { value, label: value } : undefined)

  // In server-search mode `isLoading` flips on every keystroke, so it must not
  // reach the trigger — only the list shows the spinner.
  const triggerIsLoading = isLoading && !onInputChange

  const trimmedInput = inputValue.trim()
  const showCustomValue =
    allowCustomValue &&
    trimmedInput !== "" &&
    // cmdk keys items by value, and the option rows key off their label — so
    // suppress the custom row when either would collide.
    !options.some(
      (option) => option.value === trimmedInput || option.label === trimmedInput
    )

  const handleSelect = React.useCallback((optionValue: string) => {
    onValueChange?.(optionValue)
    setOpen(false)
    setInputValue("")
  }, [onValueChange])

  const handleClear = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    onValueChange?.('')
    setOpen(false)
  }, [onValueChange])

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setInputValue("")
  }, [])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={isDisabled || triggerIsLoading}
        >
          <span className="truncate flex-1 text-left">
            {triggerIsLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
                Loading...
              </>
            ) : selectedOption ? (
              selectedOption.label
            ) : (
              placeholder
            )}
          </span>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {!triggerIsLoading && !isDisabled && value && (
              <button
                type="button"
                onClick={handleClear}
                className="rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin opacity-50" />
            ) : (
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[--radix-popover-trigger-width] p-0" 
        align="start"
        onInteractOutside={(e) => {
          // Allow interactions inside the popover
          const target = e.target as HTMLElement
          if (target.closest('[cmdk-list]') || target.closest('[cmdk-item]')) {
            e.preventDefault()
          }
        }}
      >
        <Command shouldFilter={!onInputChange}>
          <CommandInput
            placeholder="Search..."
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList>
            {/* Outside the loading branch: a value the search will never return
                is exactly the one the user needs to commit while it's pending. */}
            {showCustomValue && (
              <CommandGroup>
                <CommandItem
                  key={`__custom__${trimmedInput}`}
                  value={trimmedInput}
                  onSelect={() => handleSelect(trimmedInput)}
                >
                  <div className="mr-2 h-4 w-4 shrink-0 flex items-center justify-center">
                    <Plus className="h-4 w-4 opacity-70" />
                  </div>
                  <span className="font-semibold truncate">
                    {customValueLabel(trimmedInput)}
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
            {isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
                Loading...
              </div>
            ) : (
              <>
                {!showCustomValue && <CommandEmpty>{emptyText}</CommandEmpty>}
                <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  disabled={option.disabled}
                  onSelect={(currentValue) => {
                    // Handle keyboard selection
                    const selectedOption = options.find(
                      (opt) => opt.label === currentValue
                    )
                    if (selectedOption) {
                      handleSelect(selectedOption.value)
                    }
                  }}
                >
                  <div className="mr-2 h-4 w-4 shrink-0 flex items-center justify-center">
                    <Check
                      className={cn(
                        "h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className={cn(
                      "font-semibold",
                      option.description && "text-sm"
                    )}>
                      {option.label}
                    </span>
                    {option.description && (
                      <span className="text-xs text-muted-foreground mt-0.5">
                        {option.description}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
